import type { PopperPlacementType, PopperProps, Theme } from '@material-ui/core'
import { ButtonBase, ClickAwayListener, FormControl, Popper, makeStyles } from '@material-ui/core'
import type { Variant as TypographyVariant } from '@material-ui/core/styles/createTypography'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ClearIcon from '@material-ui/icons/Clear'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { useSnackbar } from 'notistack'
import type { ReactNode } from 'react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from '@material-ui/core/styles/withStyles'
import classNames from 'classnames'
import { useTranslation } from '../../providers/TranslationProvider'
import { filterDropdownOptions } from './filterDropdownOptions'
import {
  filterSectionGroupOptions,
  isGroupExpanded,
  isGroupFullySelected,
  isSectionGroup,
  showsGroupSelectAll,
  sortByOptionOrder,
  toggleGroupSelection
} from './groupedOptions'
import { shouldCommitPendingCreate } from './shouldCommitPendingCreate'
import { getClickAwayMouseEvent } from './getClickAwayMouseEvent'
import { focusLeftDropdown } from './focusLeftDropdown'
function slugify(text: string): string {
  if (!text) return ''
  return text
    .toString()
    .toLowerCase()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^\w-]+/g, '')
    .replaceAll(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

// How long a created option must go unchanged before it commits - guards against a stray
// Enter (e.g. mobile predictive-text) creating an option from an in-progress, unfinished value.
// 300ms is comfortably longer than the gap between characters typed in quick succession, so a
// genuine mid-typing Enter gets cancelled, while a deliberate Enter after typing stops still
// commits with no perceptible delay to the user.
const CREATE_SETTLE_MS = 300

const USER_COLOR_PALETTE = [
  '#4E7FD0',
  '#4E943E',
  '#C06325',
  '#8070CA',
  '#088CB9',
  '#139676',
  '#BA5B8B',
  '#868604',
  '#0D9298',
  '#CE5057',
  '#AD721C',
  '#A363B2'
]
import Collapse from '../Collapse'
import Skeleton from '../Skeleton'
import { useIsMobile } from '../../hooks'
import Grid from '../Grid'
import type { InputFieldProps } from '../InputField'
import InputField from '../InputField'
import InputLabel from '../InputLabel'
import Paper from '../Paper'
import Tooltip from '../Tooltip'
import Typography from '../Typography'
import Spinner from '../Spinner'
import Avatar from '../Avatar'
import DefaultDropdownSelectMenuItem from './DefaultDropdownSelectMenuItem'
import type { DropdownSelectMenuItemProps } from './DefaultDropdownSelectMenuItem'
import DropdownSelectFooter from './DropdownSelectFooter'
import DropdownSelectSearchBar from './DropdownSelectSearchBar'
import DropdownChips from './DropdownChips'

// Bottom padding of the options list, in theme spacing units. A trailing collapsible section cancels
// it with a matching negative margin, so the two must stay equal.
const MENU_BOTTOM_PADDING = 1

const useStyles = makeStyles<Theme, DropdownSelectProps>((theme: Theme) => ({
  root: {},
  inputLabel: {
    marginBottom: '5px'
  },

  tooltipTypography: {
    fontSize: '0.75rem',
    fontWeight: '500',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.65rem'
    }
  },
  toolTipLabel: {
    fontSize: '0.7rem',
    fontWeight: '500',
    marginBottom: '5px'
  },
  toolTipWrapper: {
    display: 'flex',
    alignContent: 'center',
    justifyContent: 'flex-start',
    paddingBottom: theme.spacing(0.5),
    marginTop: `-${theme.spacing(0.5)}px`
  },
  dealViewContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  menuItemsContainer: {
    maxHeight: ({ maxMenuHeight }) => `${maxMenuHeight ?? '300'}px`,
    overflow: 'auto',
    paddingBottom: theme.spacing(MENU_BOTTOM_PADDING),
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: 6
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P80,
      borderRadius: 3
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent'
    }
  },
  noOptionsMessage: {
    padding: theme.spacing(2),
    color: theme.palette.text.disabled
  },
  icon: {
    cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
    color: theme.paintscout.palette.brand.Neutral.P80,
    marginRight: -theme.spacing(1),
    fontSize: ({ shrink }) => (shrink ? '1.25rem' : '1.5rem'),
    width: ({ shrink }) => (shrink ? '24px' : '36px')
  },
  ownedByViewIcon: {
    borderRadius: '50%',
    height: theme.spacing(2),
    width: theme.spacing(2),
    cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
    color: theme.paintscout.palette.brand.Neutral.P80,
    marginLeft: theme.spacing(0.5),
    '&:hover': {
      color: ({ disabled }) =>
        disabled ? theme.paintscout.palette.brand.Neutral.P80 : theme.paintscout.palette.brand.Neutral.P35,
      backgroundColor: ({ disabled }) =>
        disabled ? theme.paintscout.palette.brand.Neutral.P94 : theme.paintscout.palette.brand.Neutral.P80
    }
  },
  selectInputRoot: ({ nestedDropdown, typographyVariant, headerTypographyVariant }) => ({
    ...theme.typography[headerTypographyVariant || typographyVariant],
    margin: 0,
    ...(nestedDropdown
      ? {
          border: 'none !important',
          backgroundColor: 'transparent'
        }
      : {}),
    boxShadow: nestedDropdown ? 'none !important' : undefined,
    caretColor: 'transparent',
    // Safari mobile: skip the synthetic 300ms tap-delay so the dropdown opens
    // on the first tap. The trigger is a readOnly input (see #20209), which
    // iOS treats with extra heuristics — manipulation makes the tap behave
    // like a plain click.
    touchAction: 'manipulation'
  }),
  selectInput: {
    borderRadius: theme.borderRadius.md,
    cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
    paddingRight: 0,
    textTransform: ({ inputTextTransform }) => inputTextTransform || 'none',
    color: ({ inputTextColor, disabled }) =>
      inputTextColor || (disabled ? theme.palette.text.disabled : theme.palette.text.primary),
    padding: ({ shrink }) => (shrink ? theme.spacing(0.25, 1) : undefined)
  },
  openSelectInput: {
    borderRadius: theme.borderRadius.md,
    borderColor: theme.paintscout.palette.primary + ' !important',
    boxShadow: `0 0 0 1px ${theme.paintscout.palette.primary} !important`
  },
  popper: {
    zIndex: theme.zIndex.modal + 1,
    boxShadow: theme.shadows[4],
    marginTop: '1px'
  },
  sublabel: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  paper: {
    paddingTop: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    position: 'relative',
    // Safari mobile: skip the 300ms double-tap-zoom delay on every option
    // inside the popper. Without this, the first tap on an option can be
    // swallowed when a parent form does any focus/layout work mid-tap.
    touchAction: 'manipulation'
  },
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1
  },
  closeButton: {
    padding: theme.spacing(0.5),
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary
    }
  },
  groupLabel: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96
  },
  collapsibleSection: {
    borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    // As the last group, run the grey into the list's bottom padding instead of leaving a white strip.
    '&:last-child': {
      marginBottom: -theme.spacing(MENU_BOTTOM_PADDING),
      paddingBottom: theme.spacing(MENU_BOTTOM_PADDING)
    }
  },
  collapsibleHeader: {
    width: '100%',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    minHeight: 48,
    padding: theme.spacing(0, 2),
    fontWeight: 600,
    color: theme.paintscout.palette.brand.Neutral.P35
  },
  collapsibleChevron: {
    fontSize: '1.1rem',
    transition: 'transform 150ms'
  },
  collapsibleChevronOpen: {
    transform: 'rotate(90deg)'
  },
  collapsibleBadge: {
    padding: theme.spacing(0, 0.75),
    borderRadius: 8,
    fontSize: '0.6875rem',
    lineHeight: '16px',
    fontWeight: 600,
    backgroundColor: theme.paintscout.palette.brand.Neutral.P90
  },
  // This is a hack to get the helper text to display above the input.
  // We can use it to apply formatOptionLabel to the selected value.
  helperText: {
    position: 'absolute',
    top: '10%',
    left: 2,
    width: 'calc(100% - 38px)',
    height: '80%',
    backgroundColor: ({ disabled }) =>
      disabled ? theme.paintscout.palette.brand.Neutral.P94 : theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    // allow clicks to pass through to the input
    pointerEvents: 'none'
  },
  hiddenFocus: {
    border: 'none',
    boxShadow: 'none'
  },
  adornment: {
    marginLeft: 0
  },
  ownedByViewAdornment: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(0.5),
    borderRadius: theme.borderRadius.md,
    '& svg': {
      color: theme.paintscout.palette.brand.Neutral.P50,
      '&:hover': {
        color: theme.paintscout.palette.brand.Neutral.P50
      }
    },
    '&:hover': {
      color: theme.palette.primary.main,
      '& svg': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        color: theme.paintscout.palette.brand.Neutral.P50
      }
    }
  },
  profileImage: {
    borderRadius: '50%',
    objectFit: 'cover',
    fontSize: '1.5em',
    width: '1.2em',
    height: '1.2em',
    margin: theme.spacing(),
    marginLeft: 0
  },
  fillerIcon: {
    margin: 0,
    padding: 0,
    marginRight: `-${theme.spacing(0.5)}px`
  },
  ownedDropdown: {
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      cursor: ({ disabled }) => (disabled ? 'default' : 'pointer'),
      color: theme.palette.primary.main,
      '& svg': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        color: theme.paintscout.palette.brand.Neutral.P50
      }
    }
  }
}))

export interface DropdownSelectPlaceholder {
  selectPlaceholder?: string // Select component placeholder
  plural?: string // Used in the dropdown's search bar
}

export interface DropdownSelectOption {
  key?: string
  label: string
  value: any
  sublabel?: string
  sublabelMuted?: boolean
  /** A deactivated user: the menu item greys the avatar, mutes the sublabel and hides the role. */
  inactive?: boolean
  dotColor?: string
  disabled?: boolean
  forceTop?: boolean
  picture?: string
  tooltip?: string
  [key: string]: any
}

export interface GroupedDropdownSelectOption {
  label: string
  options: DropdownSelectOption[]
  /**
   * Renders a select-all row for just this group (e.g. "Select all deactivated"). When any group sets
   * it, the global "Select All" row is hidden. Groups that set this, `collapsible` or `hideLabel` also
   * narrow their own children by search.
   */
  selectAllLabel?: string
  /** Renders the header as a toggle with a count badge. Starts collapsed; opens while a search matches. */
  collapsible?: boolean
  /** Renders the group without a header. */
  hideLabel?: boolean
}

interface BaseDropdownSelectProps extends Omit<InputFieldProps, 'onChange' | 'placeholder' | 'helperText'> {
  label?: string
  options?: DropdownSelectOption[] | GroupedDropdownSelectOption[]
  autoApply?: boolean
  placeholder?: DropdownSelectPlaceholder
  loading?: boolean
  toolTip?: string | ReactNode
  disabled?: boolean
  searchable?: boolean
  searchCallback?: (queryText: string) => void
  noOptionsMessage?: string
  hideIcon?: boolean
  popperPlacement?: PopperPlacementType
  popperModifiers?: PopperProps['modifiers']
  disablePortal?: boolean
  // Use popper.js fixed positioning — needed when portaled out of a position:fixed
  // container (e.g. a Dialog) so the menu stays anchored to the input while the page is scrolled.
  positionFixed?: boolean
  nestedDropdown?: boolean // For components like the Discount Rate Dropdown
  maxMenuHeight?: number
  typographyVariant?: TypographyVariant
  headerTypographyVariant?: TypographyVariant
  textWidth?: number // The width to make text for cutoff in dropdown input
  /**
   * Floors the popup menu's width below the trigger's own width -- the menu otherwise always
   * matches `selectRef.clientWidth + 36/42px`, so a field narrow enough to fit a short selected
   * value (e.g. a sidebar's "Default font" row) forces every menu option to truncate at that same
   * narrow width regardless of how long its label is (ENG-1395).
   */
  menuMinWidth?: number
  inputTextTransform?: CSSProperties['textTransform']
  inputTextColor?: CSSProperties['color']
  shrink?: boolean
  pagination?: {
    currentPage: number
    pages: ReactNode[]
    onPageChange?: (page: number) => void
  }
  minimumSearchCharacters?: number
  hideSelectAll?: boolean
  showCollapsibleChips?: boolean
  autoChipsOpen?: boolean
  trimLabel?: { length: number; ellipsis?: boolean }
  ownedByView?: boolean
  dealView?: boolean
  customOwnedByLabel?: ReactNode
  denseOptions?: boolean
  onMinimumSearchCharactersReached?: (searchFilter: string) => void
  validateCreatedOption?: (option: string) => string
  checkInvalid?: () => string
  createLabel?: (val: string) => string
  formatOptionLabel?: (option: DropdownSelectOption, placement?: 'menu-item' | 'selected') => React.ReactNode
  onCreateOption?: (option: string) => void
  createAtTop?: boolean
  hasUnselect?: boolean
  hideFooter?: boolean
  hideCloseButton?: boolean
  hideDoneButton?: boolean
  renderMenuItem?: React.ComponentType<DropdownSelectMenuItemProps>
  /**
   * When true, option labels wrap to multiple lines instead of truncating with an
   * ellipsis. Useful for long labels (e.g. product names with a sheen suffix) that
   * would otherwise be cut off in a narrow dropdown.
   */
  wrapOptionLabel?: boolean
}

export interface SingleSelectProps extends BaseDropdownSelectProps {
  variant: 'single'
  value: DropdownSelectOption
  onChange: (option: DropdownSelectOption) => void
}

export interface MultiSelectProps extends BaseDropdownSelectProps {
  variant: 'multi'
  value: DropdownSelectOption[]
  onChange: (options: DropdownSelectOption[]) => void
}

export type DropdownSelectProps = SingleSelectProps | MultiSelectProps

export default function DropdownSelect(props: DropdownSelectProps) {
  const classes = useStyles(props)
  const { __ } = useTranslation()

  const {
    label,
    placeholder,
    toolTip,
    disabled,
    hideIcon,
    variant,
    loading,
    autoApply = true,
    noOptionsMessage,
    nestedDropdown,
    typographyVariant = 'body1',
    hideSelectAll,
    formatOptionLabel,
    createLabel,
    validateCreatedOption,
    checkInvalid = () => '',
    onChange: onChangeProp,
    onCreateOption,
    onMinimumSearchCharactersReached,
    popperModifiers,
    disablePortal = true,
    positionFixed = false,
    pagination,
    popperPlacement = 'bottom-start',
    sublabel,
    minimumSearchCharacters,
    searchable: propsSearchable,
    searchCallback,
    showCollapsibleChips,
    autoChipsOpen,
    trimLabel,
    ownedByView,
    dealView,
    customOwnedByLabel,
    denseOptions,
    createAtTop,
    hasUnselect,
    hideFooter,
    hideCloseButton = true,
    hideDoneButton = false,
    renderMenuItem: MenuItemComponent = DefaultDropdownSelectMenuItem,
    wrapOptionLabel,
    menuMinWidth,
    ...inputProps
  } = props

  const multi = variant === 'multi'
  const searchable = propsSearchable || !!minimumSearchCharacters

  const [anchorEl, setAnchorEl] = useState(null)
  const selectRef = useRef<HTMLInputElement>(null)
  const paperRef = useRef<HTMLDivElement>(null)
  const [searchFilter, setSearchFilter] = useState('')
  const [allSelected, setAllSelected] = useState(false)
  // Open/closed state of `collapsible` groups, keyed by group label. Kept here rather than in
  // MenuContent, which is re-created on every render and would lose it.
  const [toggledGroups, setToggledGroups] = useState<Record<string, boolean>>({})
  const [hasDeletedChips, setHasDeletedChips] = useState(false)
  const mobile = useIsMobile()
  const { enqueueSnackbar } = useSnackbar()
  const [intermediateValue, setIntermediateValue] = useState<DropdownSelectProps['value']>(
    props.value ?? (multi ? [] : null)
  )
  // Holds a create-option commit deferred by onCreateClick until CREATE_SETTLE_MS passes
  // without further typing - see onCreateClick/handleSearchFilterChange. Independent of any
  // debounce a consumer applies to its own onChange/onMinimumSearchCharactersReached handling -
  // this timer only gates the create-commit, not the search filter itself.
  const pendingCreateRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // onCreateOption/autoApply are read from these refs (not closed over) when the deferred create
  // fires, so a parent re-render during the settle window doesn't use a stale prop value.
  const onCreateOptionRef = useRef(onCreateOption)
  useEffect(() => {
    onCreateOptionRef.current = onCreateOption
  }, [onCreateOption])
  const autoApplyRef = useRef(autoApply)
  useEffect(() => {
    autoApplyRef.current = autoApply
  }, [autoApply])
  useEffect(() => {
    return () => {
      if (pendingCreateRef.current) {
        clearTimeout(pendingCreateRef.current)
      }
    }
  }, [])

  // Track the width of the select input to determine if the selected options are overflowing
  // Must be done in state via useEffect to trigger re-renders as refs don't cause re-renders
  const [selectWidth, setSelectWidth] = useState<number | null>(null)
  useEffect(() => {
    setSelectWidth(selectRef.current?.clientWidth || null)
  }, [selectRef.current?.clientWidth])

  const ownedByUrl =
    !multi &&
    (props?.value?.customAvatar ? props?.value?.customAvatar : props?.value?.picture ? props?.value?.picture : '')
  const ownedByName = !multi && props?.value?.label ? props?.value?.label : ''
  const ownedByColor = !multi && props?.value?.color ? props?.value?.color : USER_COLOR_PALETTE[0]
  const ownedByInactive = !multi && !!props?.value?.inactive

  useEffect(() => {
    if (searchCallback) {
      searchCallback(searchFilter)
    }
  }, [searchFilter])

  const optionsAreGrouped = (
    options: DropdownSelectOption[] | GroupedDropdownSelectOption[]
  ): options is GroupedDropdownSelectOption[] => {
    const groupedOptions = (options as GroupedDropdownSelectOption[]) || []
    return groupedOptions.some((option) => optionIsGrouped(option))
  }

  const optionIsGrouped = (
    option: DropdownSelectOption | GroupedDropdownSelectOption
  ): option is GroupedDropdownSelectOption => {
    return (option as GroupedDropdownSelectOption).options !== undefined
  }

  // "Select All" needs the real, selectable addresses, not the group wrappers themselves
  const flattenOptions = (options: DropdownSelectOption[] | GroupedDropdownSelectOption[]): DropdownSelectOption[] =>
    (options || []).reduce<DropdownSelectOption[]>(
      (flattened, option) => flattened.concat(optionIsGrouped(option) ? option.options : option),
      []
    )

  const value = autoApply ? props.value ?? (multi ? [] : null) : intermediateValue

  const calculatedOptions =
    multi && !optionsAreGrouped(props.options)
      ? [
          ...props.options.filter((option) => option.forceTop),
          ...props.value.filter((selectedOption) => !selectedOption.forceTop),
          ...props.options.filter(
            (option) =>
              !(props.value as DropdownSelectOption[]).some((selectedOption) =>
                selectedOption.key ? selectedOption.key === option.key : selectedOption.value === option.value
              ) && !option.forceTop
          )
        ]
      : props.options

  const [options, setOptions] = useState<DropdownSelectProps['options']>(calculatedOptions)

  // Flattened, real, selectable options (grouped options expanded) — used by "Select All" and by
  // the "all selected" checks below, so both stay in sync and neither recomputes redundantly.
  const flatOptions = useMemo(() => flattenOptions(options), [options])
  const allOptionsSelected =
    multi && flatOptions.length > 1 && flatOptions.length === (value as DropdownSelectOption[])?.length

  // whenever the dropdown is opened, reset the options to the calculated options
  useEffect(() => {
    setOptions(calculatedOptions)
  }, [anchorEl, intermediateValue])

  // keep options in sync with calculated options when an option is created
  useEffect(() => {
    if (calculatedOptions?.length > options?.length) {
      setOptions(calculatedOptions)
    }
  }, [calculatedOptions, options])

  // keep options in sync with calculated options when an option is disabled
  useEffect(() => {
    if (!optionsAreGrouped(calculatedOptions) && !optionsAreGrouped(options)) {
      const optionsNeedToGetDisabled = calculatedOptions.some((calculatedOptions) => {
        const matchingOption = options.find((o) =>
          o.key ? o.key === calculatedOptions.key : o.value === calculatedOptions.value
        )
        return matchingOption && calculatedOptions.disabled !== matchingOption.disabled
      })

      if (optionsNeedToGetDisabled) {
        const calculatedOptionsOrderedByOptions = options.map((option) => {
          const matchingOption = calculatedOptions.find((o) =>
            o.key ? o.key === option.key : o.value === option.value
          )
          return matchingOption || option
        })

        setOptions(calculatedOptionsOrderedByOptions)
      }
    }
  }, [calculatedOptions, options])

  useEffect(() => {
    if (!autoApply) {
      setIntermediateValue(props.value ?? (multi ? [] : null))
      if (!props?.value?.length && allSelected) {
        setAllSelected(false)
      }
    }
  }, [props.value])

  // If we're async the options are changing, so keep them in sync
  useEffect(() => {
    setOptions(calculatedOptions)
  }, [props.options])

  // Keep "Select All" state aligned with the current value.
  useEffect(() => {
    if (!multi) {
      return
    }
    if (allSelected !== allOptionsSelected) {
      setAllSelected(allOptionsSelected)
    }
  }, [allOptionsSelected, multi, allSelected])

  // If the anchor element changes, we need to call onFocus and onBlur
  useEffect(() => {
    if (anchorEl && typeof props.onFocus === 'function') {
      props.onFocus({ target: selectRef.current } as any)
    }
    if (!anchorEl && typeof props.onBlur === 'function') {
      props.onBlur({ target: selectRef.current } as any)
    }
  }, [anchorEl])

  const getReadableValue = () => {
    if (multi) {
      return (value as DropdownSelectOption[]).map((option) => option.label).join(', ')
    } else {
      return (value as DropdownSelectOption)?.label || ''
    }
  }

  const selectedOptionsAreOverflowing = () => {
    if (!selectWidth) {
      return false
    }
    const readableValue = getReadableValue()
    const textWidth = readableValue.length * (props?.textWidth || 8.25) // 8px ish per character plus a buffer
    return textWidth > selectWidth
  }

  const getDisplayValue = () => {
    // If all options are selected, display 'All Selected'
    // extra check for multi select to ensure that the display value doesn't flicker when all options are selected
    if (multi && !minimumSearchCharacters && (allSelected || allOptionsSelected)) {
      return __('All Selected')
    } else if (multi && selectedOptionsAreOverflowing() && (value as DropdownSelectOption[]).length > 1) {
      return __('%d Selected', (value as DropdownSelectOption[]).length)
    } else {
      if (selectedOptionsAreOverflowing() && trimLabel) {
        return `${getReadableValue().substring(0, trimLabel.length)}${trimLabel.ellipsis ? '...' : ''}`
      }
      return getReadableValue()
    }
  }

  const optionIsSelected = (option: DropdownSelectOption) => {
    const compareValue = option.key ? 'key' : 'value'
    if (multi) {
      return !!(value as DropdownSelectOption[]).find(
        (selectedOption) => selectedOption[compareValue] === option[compareValue]
      )
    } else {
      return (value as DropdownSelectOption)?.[compareValue] === option[compareValue]
    }
  }

  const isMultiSelectHandler = (onChangeProp: any): onChangeProp is (options: DropdownSelectOption[]) => void => multi

  const isSingleSelectHandler = (onChangeProp: any): onChangeProp is (option: DropdownSelectOption) => void => !multi

  const filterOptions = () =>
    filterDropdownOptions<DropdownSelectOption | GroupedDropdownSelectOption>({
      options,
      searchFilter,
      minimumSearchCharacters
    })

  const onChange = (options: DropdownSelectOption[] | DropdownSelectOption) => {
    if (isMultiSelectHandler(onChangeProp)) {
      onChangeProp(options as DropdownSelectOption[])
    } else if (isSingleSelectHandler(onChangeProp)) {
      onChangeProp((Array.isArray(options) ? options[0] : options) as DropdownSelectOption)
    }
  }

  const handleChange = (options) => {
    if (autoApply) {
      onChange(options)
    } else {
      setIntermediateValue(options)
    }
  }

  const handleChipDelete = (option: DropdownSelectOption) => {
    setHasDeletedChips(true)
    const newOptions = (value as DropdownSelectOption[]).filter((selectedOption) =>
      selectedOption.key ? selectedOption.key !== option.key : selectedOption.value !== option.value
    )
    onChange(newOptions)
    console.log('deleting chip')
    setAllSelected(false)
  }

  const handleAllOptionClick = () => {
    if (allSelected) {
      handleChange([])
      setAllSelected(false)
    } else {
      handleChange(flatOptions)
      setAllSelected(true)
    }
  }

  const handleOptionClick = (option) => {
    if (multi) {
      if (optionIsSelected(option)) {
        setAllSelected(false)
        handleChange(
          (value as DropdownSelectOption[]).filter((selectedOption) =>
            selectedOption.key ? selectedOption.key !== option.key : selectedOption.value !== option.value
          )
        )
      } else {
        handleChange([...(value as DropdownSelectOption[]), option])
      }
    } else {
      handleChange([option])
      if (!pagination) {
        handleApply({ allowCreateOption: false })
      }
    }
  }

  const handleApply = ({ allowCreateOption }: { allowCreateOption: boolean }) => {
    const invalidReason = checkInvalid()
    if (invalidReason.length) {
      enqueueSnackbar(invalidReason, { variant: 'error' })
      return
    }

    if (onCreateOption && searchable && !!searchFilter.trim() && allowCreateOption) {
      const invalidMessage = validateCreatedOption?.(searchFilter)
      if (invalidMessage) {
        enqueueSnackbar(invalidMessage, { variant: 'error' })
        return
      }
      onCreateOption(searchFilter)
    }
    if (!autoApply) {
      onChange(intermediateValue)
    }

    setAnchorEl(null)
    clearSearchFilter()
  }

  const emptyValue = multi ? ([] as DropdownSelectOption[]) : (null as DropdownSelectOption | null)

  const handleClear = () => {
    onChange(emptyValue as DropdownSelectProps['value'])
    setIntermediateValue(emptyValue)
    setAllSelected(false)
    if (notOnDefaultPage && !!pagination?.onPageChange) {
      pagination.onPageChange(pagination.currentPage - 1)
    } else {
      clearSearchFilter()
    }
  }

  const onCreateClick = () => {
    if (!onCreateOption) {
      return
    }

    const invalidMessage = validateCreatedOption?.(searchFilter)
    if (invalidMessage) {
      enqueueSnackbar(invalidMessage, { variant: 'error' })
      return
    }

    // Defer the actual creation instead of committing searchFilter immediately: a stray
    // Enter keydown (e.g. mobile predictive-text firing mid-typing) would otherwise create
    // a tag from whatever partial value is in the box at that instant. handleSearchFilterChange
    // cancels this pending create if the user keeps typing, so only a settled value commits.
    const valueToCreate = searchFilter
    if (pendingCreateRef.current) {
      clearTimeout(pendingCreateRef.current)
    }
    pendingCreateRef.current = setTimeout(() => {
      pendingCreateRef.current = null
      // Read options fresh from state (not the closure captured at Enter-press time) since
      // they may have changed during the settle window.
      setOptions((currentOptions) =>
        optionsAreGrouped(currentOptions)
          ? ([{ label: valueToCreate, value: valueToCreate } as any, ...currentOptions] as typeof currentOptions)
          : currentOptions
      )
      onCreateOptionRef.current?.(valueToCreate)
      setSearchFilter('')
      if (autoApplyRef.current && !multi) {
        setAnchorEl(null)
      }
    }, CREATE_SETTLE_MS)
  }

  const handleSearchFilterChange = (newSearchFilter: string) => {
    if (pendingCreateRef.current) {
      clearTimeout(pendingCreateRef.current)
      pendingCreateRef.current = null
    }
    setSearchFilter(newSearchFilter)
    if (
      newSearchFilter.length >= minimumSearchCharacters &&
      !!minimumSearchCharacters &&
      !!onMinimumSearchCharactersReached
    ) {
      onMinimumSearchCharactersReached(newSearchFilter)
    }
  }

  const clearSearchFilter = () => {
    setTimeout(() => setSearchFilter(''), 200) // Delay to allow the popover to close before clearing the search filter
  }

  // Clicking outside the popper (including on a submit button) used to throw away whatever the
  // user had typed. Commit it when the select knows how to validate a created option.
  const hasPendingCreate = shouldCommitPendingCreate({
    searchable,
    searchFilter,
    onCreateOption,
    validateCreatedOption
  })

  const handleClickAway = () => {
    if (anchorEl) {
      handleApply({ allowCreateOption: hasPendingCreate })
    }
  }

  // Keyboard counterpart of the mousedown click-away (#24592): tabbing from an open non-autoApply
  // menu to a submit button never fires a click-away, so commit the picks when focus leaves.
  const handleFocusLeave = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget instanceof Node ? e.relatedTarget : null
    if (anchorEl && !autoApply && focusLeftDropdown(next, [e.currentTarget, paperRef.current])) {
      handleApply({ allowCreateOption: hasPendingCreate })
    }
  }

  const handleUnselect = (ev) => {
    ev.stopPropagation()
    onChange(emptyValue as DropdownSelectProps['value'])
  }

  const hasValue = multi ? (value as DropdownSelectOption[])?.length > 0 : !!(value as DropdownSelectOption)?.value

  const notOnDefaultPage = !!pagination && pagination.pages.length && pagination.currentPage !== 0
  const shouldShowHeaderActions = !hideCloseButton
  const shouldShowFooter = !hideFooter && ((multi && hasValue) || (!multi && !autoApply))

  const CreateOption = () => (
    <Grid key={'create-option'} item xs={12}>
      <MenuItemComponent
        typographyVariant={typographyVariant}
        searchable={searchable}
        option={{
          label: createLabel ? createLabel(searchFilter) : __('Create "%s"', searchFilter),
          value: searchFilter
        }}
        selected={false}
        showSelectableIcon
        onClick={onCreateClick}
      />
    </Grid>
  )

  const MenuContent = () => {
    if (notOnDefaultPage) {
      return pagination.pages[pagination.currentPage - 1]
    }

    const filteredOptions = filterOptions()
    const showCreateableOption = !!onCreateOption && searchable && !!searchFilter.trim()
    // Groups with their own select-all row replace the global one.
    const hasGroupSelectAll = ((options ?? []) as GroupedDropdownSelectOption[]).some(
      (option) => optionIsGrouped(option) && !!option.selectAllLabel
    )

    const renderGroupedMenuItem = (option: DropdownSelectOption, index: number, groupedIndex: number) => (
      <MenuItemComponent
        dense={denseOptions}
        typographyVariant={typographyVariant}
        searchable={searchable}
        wrapOptionLabel={wrapOptionLabel}
        key={`${option.key ?? option.value}-${index}-${groupedIndex}`}
        option={option}
        showSelectableIcon={multi}
        formatOptionLabel={
          formatOptionLabel ? (option) => formatOptionLabel?.(option, 'menu-item') || option.label : null
        }
        selected={multi && optionIsSelected(option)}
        onClick={() => handleOptionClick(option)}
      />
    )

    const renderSectionGroup = (group: GroupedDropdownSelectOption, index: number) => {
      const groupOptions = filterSectionGroupOptions(group, searchFilter)
      const expanded = isGroupExpanded({
        collapsible: group.collapsible,
        toggledOpen: toggledGroups[group.label],
        searchFilter,
        matchCount: groupOptions.length
      })
      const selectAllRow = multi && showsGroupSelectAll(group) && (
        <MenuItemComponent
          bold
          typographyVariant={typographyVariant}
          searchable={searchable}
          option={{ label: group.selectAllLabel, value: `select-all-${group.label}` }}
          showSelectableIcon
          selected={isGroupFullySelected(group.options, value as DropdownSelectOption[])}
          onClick={() => handleChange(toggleGroupSelection(value as DropdownSelectOption[], group.options))}
        />
      )

      return (
        <Grid
          key={`${group.label}-${index}`}
          item
          xs={12}
          className={group.collapsible ? classes.collapsibleSection : undefined}
        >
          {group.collapsible ? (
            <ButtonBase
              className={classes.collapsibleHeader}
              aria-expanded={expanded}
              onClick={() => setToggledGroups((prev) => ({ ...prev, [group.label]: !expanded }))}
            >
              <ChevronRightIcon
                className={classNames(classes.collapsibleChevron, { [classes.collapsibleChevronOpen]: expanded })}
              />
              <Typography variant="body2" component="span" style={{ fontWeight: 600 }}>
                {group.label}
              </Typography>
              <span className={classes.collapsibleBadge}>{group.options.length}</span>
            </ButtonBase>
          ) : (
            !group.hideLabel && (
              <Typography variant="h6" className={classes.groupLabel}>
                {group.label}
              </Typography>
            )
          )}
          {expanded && (
            <>
              {selectAllRow}
              {groupOptions.map((option, groupedIndex) => renderGroupedMenuItem(option, index, groupedIndex))}
            </>
          )}
        </Grid>
      )
    }

    return (
      <>
        {multi && filteredOptions.length > 1 && !searchFilter && !hideSelectAll && !hasGroupSelectAll && (
          <Grid key={`select-all`} item xs={12}>
            <MenuItemComponent
              bold
              typographyVariant={typographyVariant}
              searchable={searchable}
              option={{ label: __('Select All'), value: 'select-all' }}
              showSelectableIcon={multi}
              selected={allSelected}
              onClick={handleAllOptionClick}
            />
          </Grid>
        )}
        {showCreateableOption && createAtTop && <CreateOption />}
        {filteredOptions.length > 0 &&
          filteredOptions.map((option: GroupedDropdownSelectOption | DropdownSelectOption, index) => {
            if (optionIsGrouped(option)) {
              if (isSectionGroup(option)) {
                return renderSectionGroup(option, index)
              }
              return (
                <Grid key={`${option.label}-${index}`} item xs={12}>
                  <Typography variant="h6" className={classes.groupLabel}>
                    {option.label}
                  </Typography>
                  {(option as GroupedDropdownSelectOption).options.map((option, groupedIndex) =>
                    renderGroupedMenuItem(option, index, groupedIndex)
                  )}
                </Grid>
              )
            } else {
              const unGroupedOption = option as DropdownSelectOption
              return (
                <Grid
                  className={label ? `${slugify(label)}-option-${index}` : ''}
                  key={`${unGroupedOption.value}-${index}`}
                  item
                  xs={12}
                >
                  <MenuItemComponent
                    dense={denseOptions}
                    searchable={searchable}
                    typographyVariant={typographyVariant}
                    wrapOptionLabel={wrapOptionLabel}
                    option={unGroupedOption}
                    formatOptionLabel={
                      formatOptionLabel ? (option) => formatOptionLabel?.(option, 'menu-item') || option.label : null
                    }
                    selected={multi && optionIsSelected(unGroupedOption)}
                    showSelectableIcon={multi}
                    onClick={() => handleOptionClick(unGroupedOption)}
                  />
                </Grid>
              )
            }
          })}
        {showCreateableOption && !createAtTop && <CreateOption />}
        {!showCreateableOption && !filteredOptions.length && (
          <Grid className={classes.noOptionsMessage} container justifyContent="center">
            <Typography variant="caption">
              {!!minimumSearchCharacters && searchFilter.length < minimumSearchCharacters
                ? __('Search term must be %d characters or more.', minimumSearchCharacters)
                : noOptionsMessage ?? __('No options')}
            </Typography>
          </Grid>
        )}
      </>
    )
  }

  const EndAdornment: React.FC<{ ownedByViewAdornment?: boolean }> = ({ ownedByViewAdornment }) => {
    // In normal (input) mode the chevron toggles the menu itself and stops
    // propagation. Previously it only ran handleClickAway (a no-op while closed),
    // so opening relied on the click bubbling to the input and on the document
    // ClickAwayListener not racing the open re-render — which left the closed
    // chevron effectively dead on some devices/desktop (#20209). ownedByView
    // keeps delegating the toggle to its parent container's onClick.
    const handleIconClick = ownedByViewAdornment
      ? handleClickAway
      : (e: React.MouseEvent) => {
          e.stopPropagation()
          if (anchorEl) {
            handleClickAway()
          } else if (!disabled && !loading) {
            setAnchorEl(selectRef.current)
          }
        }

    if (hideIcon) {
      return null
    } else if (anchorEl && !disabled && !loading) {
      return (
        <ExpandLessIcon
          className={classNames({
            [classes.icon]: !ownedByViewAdornment,
            [classes.ownedByViewIcon]: ownedByViewAdornment
          })}
          onClick={handleIconClick}
        />
      )
    } else if (loading) {
      return <Spinner size={20} />
    } else if (hasUnselect && hasValue && !disabled) {
      return (
        <ClearIcon
          className={classNames({
            [classes.icon]: !ownedByViewAdornment,
            [classes.ownedByViewIcon]: ownedByViewAdornment
          })}
          onClick={handleUnselect}
        />
      )
    } else {
      return (
        <ExpandMoreIcon
          className={classNames({
            [classes.icon]: !ownedByViewAdornment,
            [classes.ownedByViewIcon]: ownedByViewAdornment
          })}
          onClick={handleIconClick}
        />
      )
    }
  }

  return (
    // Listen on mousedown while click-away has a value to commit (a pending create, or an open
    // non-autoApply select's picks) so it lands before an outside submit button's onClick.
    <ClickAwayListener
      onClickAway={handleClickAway}
      mouseEvent={getClickAwayMouseEvent({ hasPendingCreate, autoApply })}
    >
      <FormControl fullWidth className={classes.root} onBlur={handleFocusLeave}>
        {label &&
          (toolTip ? (
            <>
              <div className={classes.toolTipWrapper}>
                <Tooltip
                  interactive={typeof toolTip !== 'string'}
                  content={
                    typeof toolTip === 'string' ? <span dangerouslySetInnerHTML={{ __html: toolTip }} /> : toolTip
                  }
                >
                  <Typography className={classes.tooltipTypography} variant="overline">
                    {label}
                    {inputProps.required && <span style={{ color: 'red' }}> *</span>}
                  </Typography>
                </Tooltip>
              </div>
              {sublabel && <InputLabel variant="sublabel">{sublabel}</InputLabel>}
            </>
          ) : (
            <>
              <InputLabel required={inputProps.required} className={sublabel ? null : classes.inputLabel} shrink>
                {label}
              </InputLabel>
              {sublabel && <InputLabel variant="sublabel">{sublabel}</InputLabel>}
            </>
          ))}
        {ownedByView ? (
          loading ? (
            <Skeleton width="240px" height="25px" />
          ) : (
            <div
              className={classes.ownedDropdown}
              onClick={(e) => {
                if (!anchorEl && !disabled && !loading) {
                  setAnchorEl(e.currentTarget)
                } else {
                  setAnchorEl(null)
                }
              }}
            >
              {ownedByUrl ? (
                <img className={classes.profileImage} src={ownedByUrl} />
              ) : (
                <Avatar
                  backgroundColor="#FFFFFF"
                  disableTooltip
                  filled
                  noTransform
                  className={classes.profileImage}
                  avatarColor={ownedByColor}
                  width="28.8px"
                  height="28.8px"
                  style={{
                    fontSize: '12px',
                    paddingLeft: 0,
                    paddingTop: 0
                  }}
                >
                  {/* Limit the username to two words so we only get two initials */}
                  {ownedByName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
                </Avatar>
              )}
              {customOwnedByLabel ? (
                customOwnedByLabel
              ) : (
                <Typography variant="body1" color="textSecondary">
                  {ownedByName ? (
                    <>
                      {__('Owned by')} <strong>{ownedByName}</strong>
                    </>
                  ) : (
                    <>{__('Select User')}</>
                  )}
                </Typography>
              )}
              <div className={classes.ownedByViewAdornment}>
                <EndAdornment ownedByViewAdornment={ownedByView} />
              </div>
            </div>
          )
        ) : dealView ? (
          loading ? (
            <Skeleton width="240px" height="25px" />
          ) : (
            <div className={classes.dealViewContainer}>
              <Typography variant="subtitle2">{customOwnedByLabel ? customOwnedByLabel : __('Sales Rep:')}</Typography>
              <div
                className={classes.ownedDropdown}
                style={{ opacity: ownedByInactive ? 0.5 : 1 }}
                onClick={(e) => {
                  if (!anchorEl && !disabled && !loading) {
                    setAnchorEl(e.currentTarget)
                  } else {
                    setAnchorEl(null)
                  }
                }}
              >
                {ownedByUrl ? (
                  <img className={classes.profileImage} src={ownedByUrl} />
                ) : (
                  <Avatar
                    backgroundColor="#FFFFFF"
                    disableTooltip
                    filled
                    noTransform
                    className={classes.profileImage}
                    avatarColor={ownedByColor}
                    width="28.8px"
                    height="28.8px"
                    style={{
                      fontSize: '12px',
                      paddingLeft: 0,
                      paddingTop: 0
                    }}
                  >
                    {/* Limit the username to two words so we only get two initials */}
                    {ownedByName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
                  </Avatar>
                )}
              </div>
            </div>
          )
        ) : (
          <InputField
            {...inputProps}
            // The actual border lives on the inner MuiInput root (see Input.tsx), which the
            // `classes` prop below can't reach — InputField only forwards `className` down to it.
            // So route the nested-dropdown border/shadow removal through className.
            className={classNames((inputProps as any).className, nestedDropdown ? classes.selectInputRoot : null)}
            endAdornment={inputProps.endAdornment ?? <EndAdornment />}
            disabled={disabled}
            classes={{
              root: classNames(classes.selectInputRoot, anchorEl ? classes.openSelectInput : null),

              input: classes.selectInput,
              focused: nestedDropdown ? classes.hiddenFocus : null,
              adornment: classes.adornment
            }}
            value={getDisplayValue()}
            placeholder={loading ? __('Loading...') : placeholder?.selectPlaceholder || __('Select')}
            inputRef={selectRef}
            fullWidth
            readOnly
            inputProps={{ ...(inputProps as any).inputProps, inputMode: 'none' }}
            onMouseDown={(e) => {
              e.preventDefault()
            }}
            onClick={(e) => {
              if (!anchorEl && !disabled && !loading) {
                setAnchorEl(e.currentTarget)
              }
            }}
            helperText={
              formatOptionLabel && value ? (
                <div className={classes.helperText}>{formatOptionLabel(value as DropdownSelectOption, 'selected')}</div>
              ) : (
                ''
              )
            }
          />
        )}
        <Popper
          open={!!anchorEl && !disabled}
          className={classes.popper}
          anchorEl={anchorEl}
          placement={popperPlacement}
          disablePortal={disablePortal}
          modifiers={popperModifiers}
          popperOptions={positionFixed ? { positionFixed: true } : undefined}
        >
          <Paper
            id={'dropdown-menu-paper'}
            ref={paperRef}
            className={classes.paper}
            style={{
              width: Math.max(selectRef?.current?.clientWidth + (mobile ? 42 : 36), menuMinWidth ?? 0)
            }}
          >
            {shouldShowHeaderActions && (
              <div className={classes.header}>
                <ClearIcon
                  className={classes.closeButton}
                  onClick={() => {
                    setAnchorEl(null)
                  }}
                />
              </div>
            )}
            {searchable && !notOnDefaultPage && (
              <DropdownSelectSearchBar
                loading={loading}
                value={searchFilter}
                placeholder={placeholder?.plural ? __('Search %s', placeholder.plural) : __('Search')}
                onChange={handleSearchFilterChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !!searchFilter.trim() && !!onCreateOption) {
                    onCreateClick()
                  }
                }}
              />
            )}
            <Grid container className={classes.menuItemsContainer}>
              {MenuContent()}
            </Grid>
            {shouldShowFooter && (
              <DropdownSelectFooter
                clearLabel={notOnDefaultPage ? __('Back') : __('Clear')}
                onApply={() => handleApply({ allowCreateOption: true })}
                onClear={handleClear}
                hideDoneButton={hideDoneButton}
              />
            )}
          </Paper>
        </Popper>
        {showCollapsibleChips && multi && (selectedOptionsAreOverflowing() || allSelected || hasDeletedChips) && (
          <Collapse show={value && (value as DropdownSelectOption[]).length > 0}>
            <DropdownChips
              options={
                optionsAreGrouped(props.options)
                  ? sortByOptionOrder(value as DropdownSelectOption[], flattenOptions(props.options))
                  : (value as DropdownSelectOption[])
              }
              handleDelete={handleChipDelete}
              autoOpen={autoChipsOpen}
            />
          </Collapse>
        )}
      </FormControl>
    </ClickAwayListener>
  )
}
